import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SearchIcon from '@mui/icons-material/Search';

import { CalendarEventDto } from '../../../types/calendar';
import { foodService, FoodSuggestion, FoodResponseDto, AvailableMeasure } from '@services/food.service.ts';
import { StructuredWorkoutIcon } from '../../icons';

import { NutritionCells } from '../../common/NutritionCells';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { BaseDialog } from '@components/common/BaseDialog';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';

interface AddFoodEventModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    foodId?: number;
    customName?: string;
    name: string;
    measureKey: string;
    quantity: number;
    calendarId?: number;
    scheduledAt: Date;
    isCompleted: boolean;
    // нутрієнти на 100г для кастомного продукту
    caloriesPer100g?: number;
    proteinPer100g?: number;
    fatPer100g?: number;
    carbsPer100g?: number;
    fiberPer100g?: number;
  }) => void;
  calendarId?: number;
  eventToEdit?: CalendarEventDto | null;
  initialScheduledAt?: Date;
}

export const AddFoodEventModal: React.FC<AddFoodEventModalProps> = ({ open, onClose, onSave, calendarId, eventToEdit, initialScheduledAt }) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FoodSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingFoodDetails, setLoadingFoodDetails] = useState(false);

  const [selectedFood, setSelectedFood] = useState<FoodSuggestion | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true); 

  const [measureKey, setMeasureKey] = useState('');
  const [quantity, setQuantity] = useState('');
  // custom food nutrition per 100g
  const [customKcal, setCustomKcal] = useState('');
  const [customProtein, setCustomProtein] = useState('');
  const [customFat, setCustomFat] = useState('');
  const [customCarbs, setCustomCarbs] = useState('');
  const [customFiber, setCustomFiber] = useState('');
  const [foodRecipe, setFoodRecipe] = useState<FoodResponseDto | null>(null);
  const [loadingRecipe, setLoadingRecipe] = useState(false);
  const [recipeExpanded, setRecipeExpanded] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);

  const getOptionLabel = (option: FoodSuggestion): string => {
    if (!option) return '';
    return option.name;
  };

  // Reset all states when modal closes
  useEffect(() => {
    if (!open) {
      setInputValue('');
      setOptions([]);
      setSelectedFood(null);
      setMeasureKey('');
      setQuantity('100');
      setCustomKcal('');
      setCustomProtein('');
      setCustomFat('');
      setCustomCarbs('');
      setCustomFiber('');
      setFoodRecipe(null);
      setLoadingRecipe(false);
      setRecipeExpanded(false);
      setFetchError(null);
      setLoadingFoodDetails(false);
      setScheduledAt(null);
      setShowDateTimePicker(false);
      setIsInitializing(true);
    }
  }, [open]);

  // Initialize states when modal opens or eventToEdit changes
  useEffect(() => {
    if (open) {
      setIsInitializing(true);
      const now = new Date();
      
      const initDate = eventToEdit?.scheduledLdt
        ? new Date(eventToEdit.scheduledLdt)
        : (initialScheduledAt ?? now);
      setScheduledAt(initDate);
      const shouldShowPicker = Boolean(
        (eventToEdit && eventToEdit.scheduledLdt) || (!eventToEdit && initialScheduledAt),
      );
      setShowDateTimePicker(shouldShowPicker);

      if (eventToEdit) {
        if (eventToEdit.associationType === 'Food') {
          const foodId = eventToEdit.associationId;
          if (foodId) {
            setLoadingFoodDetails(true);
            foodService.getFoodForEdit(foodId)
              .then(fullFoodDetails => {
                const food: FoodSuggestion = {
                  id: fullFoodDetails.id,
                  name: fullFoodDetails.name,
                  category: fullFoodDetails.category,
                  measurementUnit: fullFoodDetails.measurementUnit,
                  rawMeasurementUnit: fullFoodDetails.rawMeasurementUnit,
                  caloriesPer100g: fullFoodDetails.caloriesPer100g || undefined,
                  proteinPer100g: fullFoodDetails.proteinPer100g,
                  fatPer100g: fullFoodDetails.fatPer100g,
                  carbsPer100g: fullFoodDetails.carbsPer100g,
                  fiberPer100g: fullFoodDetails.fiberPer100g,
                  defaultMeasurementValue: fullFoodDetails.defaultMeasurementValue,
                  gramsPerUnit: fullFoodDetails.gramsPerUnit || undefined,
                  isStructured: fullFoodDetails.isStructured,
                  isSystem: fullFoodDetails.isSystem,
                  availableMeasures: fullFoodDetails.availableMeasures,
                };
                setSelectedFood(food);
                setInputValue(fullFoodDetails.name ?? '');
                setFoodRecipe(fullFoodDetails);
                // Міра з події; якщо її немає (або вона вже недоступна продукту) - дефолтна міра продукту.
                const measures = food.availableMeasures ?? [];
                const savedKey = eventToEdit.details?.measureKey;
                if (savedKey && measures.some(m => m.key === savedKey)) {
                  setMeasureKey(savedKey);
                  setQuantity(eventToEdit.details?.quantity?.toString() || '1');
                } else if (measures.length) {
                  const defaultM = measures.find(m => m.isDefault) || measures[0];
                  setMeasureKey(defaultM.key);
                  setQuantity(defaultM.defaultQuantity.toString());
                }
                setFetchError(null);
              })
              .catch(error => {
                console.error("Error fetching food details for edit:", error);
                setFetchError(error.response?.data?.message || 'Не вдалося завантажити деталі продукту.');
                setSelectedFood(null);
                setInputValue(eventToEdit.title ?? '');
              })
              .finally(() => {
                setLoadingFoodDetails(false);
                setIsInitializing(false);
              });
          } else {
            setSelectedFood(null);
            setInputValue(eventToEdit.title ?? '');
            setMeasureKey('gram');
            setQuantity(eventToEdit.details?.quantity?.toString() || '100');
            setIsInitializing(false);
          }
        } else {
          setSelectedFood(null);
          setInputValue(eventToEdit.title ?? '');
          setMeasureKey('gram');
          setQuantity(eventToEdit.details?.quantity?.toString() || '100');
          setIsInitializing(false);
        }
      } else {
        setInputValue('');
        setOptions([]);
        setSelectedFood(null);
        setMeasureKey('');
        setQuantity('100');
        setFetchError(null);
        setLoadingFoodDetails(false);
        setIsInitializing(false);
      }
    }
  }, [open, eventToEdit]);

  const fetchFoodSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setOptions([]);
      setFetchError(null);
      return;
    }
    setLoading(true);
    setFetchError(null);
    try {
      const suggestions = await foodService.getFoodSuggestions(query);
      setOptions(suggestions);
    } catch (error: any) {
      console.error("Error fetching food suggestions:", error);
      setFetchError(error.response?.data?.message || 'Не вдалося завантажити пропозиції продуктів.');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isInitializing && inputValue && (!selectedFood || (selectedFood && getOptionLabel(selectedFood) !== inputValue))) {
      const handler = setTimeout(() => {
        fetchFoodSuggestions(inputValue);
      }, 300);
      return () => clearTimeout(handler);
    } else if (!inputValue) {
      setOptions([]);
      setFetchError(null);
    }
  }, [inputValue, fetchFoodSuggestions, selectedFood, isInitializing]);

  const handleSave = () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;

    if (!scheduledAt) {
      console.error("AddFoodEventModal: scheduledAt is null during save.");
      return;
    }

    let finalScheduledAt: Date = scheduledAt;
    let finalIsCompleted: boolean;
    const now = new Date();

    if (showDateTimePicker) {
      finalIsCompleted = false;
    } else {
      if (!eventToEdit) finalScheduledAt = now;
      finalIsCompleted = true;
    }

    const commonData = {
      name: trimmedInput,
      calendarId: eventToEdit?.id || calendarId,
      scheduledAt: finalScheduledAt,
      isCompleted: finalIsCompleted,
    };

    console.log("AddFoodEventModal: Data prepared for onSave:", commonData);

    const parsedQuantity = parseFloat(quantity);

    if (selectedFood?.id) {
      if (!measureKey || isNaN(parsedQuantity) || parsedQuantity <= 0) {
        setFetchError('Будь ласка, вкажіть дійсну кількість.');
        return;
      }
      onSave({
        ...commonData,
        foodId: selectedFood.id,
        measureKey,
        quantity: parsedQuantity,
      });
    } else {
      // Кастомний продукт — v2 з measureKey: "gram"
      if (isNaN(parsedQuantity) || parsedQuantity <= 0) {
        setFetchError('Будь ласка, вкажіть кількість в грамах.');
        return;
      }
      const kcalVal = parseFloat(customKcal);
      const proteinVal = parseFloat(customProtein);
      const fatVal = parseFloat(customFat);
      const carbsVal = parseFloat(customCarbs);
      const fiberVal = parseFloat(customFiber);
      onSave({
        ...commonData,
        customName: trimmedInput,
        measureKey: 'gram',
        quantity: parsedQuantity,
        ...(!isNaN(kcalVal) && { caloriesPer100g: kcalVal }),
        ...(!isNaN(proteinVal) && { proteinPer100g: proteinVal }),
        ...(!isNaN(fatVal) && { fatPer100g: fatVal }),
        ...(!isNaN(carbsVal) && { carbsPer100g: carbsVal }),
        ...(!isNaN(fiberVal) && { fiberPer100g: fiberVal }),
      });
    }
  };

  const handleAutocompleteChange = (_event: any, newValue: FoodSuggestion | string | null) => {
    if (typeof newValue === 'string') {
      setSelectedFood(null);
      setInputValue(newValue ?? '');
      setMeasureKey('gram');
      setQuantity('100');
    } else {
      setSelectedFood(newValue);
      setInputValue(newValue ? getOptionLabel(newValue) : '');
      setCustomKcal('');
      setCustomProtein('');
      setCustomFat('');
      setCustomCarbs('');
      setCustomFiber('');
      setFoodRecipe(null);
      setRecipeExpanded(false);
      if (newValue?.id) {
        setLoadingRecipe(true);
        foodService.getFoodForEdit(newValue.id)
          .then(full => setFoodRecipe(full))
          .catch(() => {})
          .finally(() => setLoadingRecipe(false));
      }
      if (newValue?.availableMeasures?.length) {
        const defaultM = newValue.availableMeasures.find(m => m.isDefault) || newValue.availableMeasures[0];
        setMeasureKey(defaultM.key);
        setQuantity(defaultM.defaultQuantity.toString());
      } else {
        setMeasureKey('');
        setQuantity('');
      }
    }
  };

  const isQuantityValid = (q: string) => {
    const num = parseFloat(q); // Use parseFloat
    return !isNaN(num) && num > 0;
  };

  const hasMeasures = !!(selectedFood?.availableMeasures?.length);

  const isSaveDisabled = Boolean(
    !inputValue.trim() ||
    (showDateTimePicker && !scheduledAt) ||
    (selectedFood?.id && (!measureKey || !isQuantityValid(quantity))) ||
    (!selectedFood?.id && !isQuantityValid(quantity))
  );

  // Калькулятор для обраного продукту з availableMeasures
  // Пріоритет: повні дані з getFoodForEdit (foodRecipe), fallback на FoodSuggestion
  const nutrition = useMemo(() => {
    if (!selectedFood?.id || !hasMeasures || !measureKey || !quantity) return null;
    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) return null;
    const measure = selectedFood.availableMeasures?.find(m => m.key === measureKey);
    if (!measure) return null;
    const qtyG = qty * measure.gramsEquivalent;
    const factor = qtyG / 100;
    const round1 = (v: number) => Math.round(v * 10) / 10;
    const src = foodRecipe ?? selectedFood; // повні дані мають пріоритет
    const kcal    = src.caloriesPer100g != null ? round1(Number(src.caloriesPer100g) * factor) : null;
    const protein = src.proteinPer100g  != null ? round1(Number(src.proteinPer100g)  * factor) : null;
    const fat     = src.fatPer100g      != null ? round1(Number(src.fatPer100g)       * factor) : null;
    const carbs   = src.carbsPer100g    != null ? round1(Number(src.carbsPer100g)     * factor) : null;
    const fiber   = src.fiberPer100g    != null ? round1(Number(src.fiberPer100g)     * factor) : null;
    if (kcal === null && protein === null && fat === null && carbs === null && fiber === null) return null;
    return { qtyG: round1(qtyG), kcal, protein, fat, carbs, fiber };
  }, [selectedFood, foodRecipe, hasMeasures, measureKey, quantity]);

  // Калькулятор для кастомного продукту
  const customNutrition = useMemo(() => {
    if (selectedFood?.id) return null;
    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) return null;
    const kcalVal = parseFloat(customKcal);
    const proteinVal = parseFloat(customProtein);
    const fatVal = parseFloat(customFat);
    const carbsVal = parseFloat(customCarbs);
    const fiberVal = parseFloat(customFiber);
    if (isNaN(kcalVal) && isNaN(proteinVal) && isNaN(fatVal) && isNaN(carbsVal) && isNaN(fiberVal)) return null;
    const factor = qty / 100;
    const round1 = (v: number) => Math.round(v * 10) / 10;
    return {
      qtyG: qty,
      kcal: !isNaN(kcalVal) ? round1(kcalVal * factor) : null,
      protein: !isNaN(proteinVal) ? round1(proteinVal * factor) : null,
      fat: !isNaN(fatVal) ? round1(fatVal * factor) : null,
      carbs: !isNaN(carbsVal) ? round1(carbsVal * factor) : null,
      fiber: !isNaN(fiberVal) ? round1(fiberVal * factor) : null,
    };
  }, [selectedFood, quantity, customKcal, customProtein, customFat, customCarbs, customFiber]);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      title={eventToEdit ? 'Редагувати продукт' : 'Додати продукт'}
      iDoc={ModalIndices.ADD_FOOD_EVENT}
      actions={
        <Button onClick={handleSave} variant="contained" disabled={isSaveDisabled}>
          Зберегти
        </Button>
      }
    >
        {loadingFoodDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Autocomplete
              id="food-autocomplete"
              freeSolo
              value={selectedFood}
              inputValue={inputValue}
              sx={{ mt: 1 }}
              open={autocompleteOpen}
              onOpen={() => {
                if (inputValue.length >= 2) {
                  setAutocompleteOpen(true);
                }
              }}
              onClose={() => setAutocompleteOpen(false)}
              options={options}
              loading={loading}
              loadingText="Завантаження..."
              noOptionsText="Продукт не знайдено. Натисніть Enter, щоб додати."
              getOptionLabel={(option) => typeof option === 'string' ? option : getOptionLabel(option)}
              filterOptions={(x) => x}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_event, newInputValue) => {
                setInputValue(newInputValue ?? '');
                if (!newInputValue || newInputValue.length < 2 || (selectedFood && getOptionLabel(selectedFood) === newInputValue)) {
                  setAutocompleteOpen(false);
                } else if (selectedFood && getOptionLabel(selectedFood) !== newInputValue) {
                  setSelectedFood(null);
                }
              }}
              onChange={handleAutocompleteChange}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">{option.name}</Typography>
                    {option.isStructured && (
                      <StructuredWorkoutIcon fontSize="small" color="action" />
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Знайдіть продукт в нашій базі або впишіть свій"
                  variant="standard"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {selectedFood?.isStructured && (
                          <InputAdornment position="start">
                            <StructuredWorkoutIcon fontSize="small" color="action" />
                          </InputAdornment>
                        )}
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <React.Fragment>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {!loading && !inputValue && <SearchIcon color="action" />}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
            />

            {fetchError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {fetchError}
              </Alert>
            )}

            {selectedFood?.id && selectedFood?.isStructured && hasMeasures && (
              <Box mt={3} sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                <TextField
                  label="Кількість"
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  variant="standard"
                  sx={{ flex: 1 }}
                  inputProps={{ min: "0.1", step: "0.1" }}
                />
                <FormControl variant="standard" sx={{ flex: 1 }}>
                  <InputLabel>Міра</InputLabel>
                  <Select
                    value={measureKey}
                    onChange={(e) => {
                      const key = e.target.value as string;
                      setMeasureKey(key);
                      const m = selectedFood.availableMeasures!.find(x => x.key === key);
                      if (m) setQuantity(m.defaultQuantity.toString());
                    }}
                  >
                    {selectedFood.availableMeasures!.map((m: AvailableMeasure) => (
                      <MenuItem key={m.key} value={m.key}>
                        {m.label} ({m.gramsEquivalent} г)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Блок нутрієнтів для обраного продукту */}
            {nutrition && <NutritionCells nutrition={nutrition} sx={{ mt: 2 }} />}

            {/* Зображення та рецепт для обраного продукту */}
            {selectedFood?.id && (
              <>
                {(selectedFood.imageUrl || foodRecipe?.imageUrl) && (
                  <Box mt={2}>
                    <img
                      src={(selectedFood.imageUrl || foodRecipe?.imageUrl)!}
                      alt={selectedFood.name}
                      style={{ width: '100%', maxHeight: 160, borderRadius: 8, objectFit: 'cover', display: 'block' }}
                    />
                  </Box>
                )}
                {loadingRecipe && !foodRecipe && (
                  <Box mt={1.5} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Завантаження...</Typography>
                  </Box>
                )}
                {foodRecipe?.details && (
                  <Box mt={1.5}>
                    <Box
                      onClick={() => setRecipeExpanded(v => !v)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}
                    >
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Рецепт
                      </Typography>
                      {recipeExpanded ? <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />}
                    </Box>
                    <Collapse in={recipeExpanded}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                        {foodRecipe.details}
                      </Typography>
                    </Collapse>
                  </Box>
                )}
              </>
            )}

            {/* Кастомний продукт: кількість + необов'язкові нутрієнти на 100г */}
            {!selectedFood?.id && inputValue.trim() && (
              <>
                <Box mt={3}>
                  <TextField
                    label="Кількість"
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    variant="standard"
                    fullWidth
                    inputProps={{ min: "0.1", step: "0.1" }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">г</InputAdornment>,
                    }}
                  />
                </Box>
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Поживна цінність на 100 г (необов'язково)
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                    <TextField
                      label="ккал"
                      type="number"
                      value={customKcal}
                      onChange={(e) => setCustomKcal(e.target.value)}
                      variant="standard"
                      sx={{ flex: 1 }}
                      inputProps={{ min: "0", step: "1" }}
                    />
                    <TextField
                      label="Білки"
                      type="number"
                      value={customProtein}
                      onChange={(e) => setCustomProtein(e.target.value)}
                      variant="standard"
                      sx={{ flex: 1 }}
                      inputProps={{ min: "0", step: "0.1" }}
                      InputProps={{ endAdornment: <InputAdornment position="end">г</InputAdornment> }}
                    />
                    <TextField
                      label="Жири"
                      type="number"
                      value={customFat}
                      onChange={(e) => setCustomFat(e.target.value)}
                      variant="standard"
                      sx={{ flex: 1 }}
                      inputProps={{ min: "0", step: "0.1" }}
                      InputProps={{ endAdornment: <InputAdornment position="end">г</InputAdornment> }}
                    />
                    <TextField
                      label="Вуглев."
                      type="number"
                      value={customCarbs}
                      onChange={(e) => setCustomCarbs(e.target.value)}
                      variant="standard"
                      sx={{ flex: 1 }}
                      inputProps={{ min: "0", step: "0.1" }}
                      InputProps={{ endAdornment: <InputAdornment position="end">г</InputAdornment> }}
                    />
                    <TextField
                      label="Клітк."
                      type="number"
                      value={customFiber}
                      onChange={(e) => setCustomFiber(e.target.value)}
                      variant="standard"
                      sx={{ flex: 1 }}
                      inputProps={{ min: "0", step: "0.1" }}
                      InputProps={{ endAdornment: <InputAdornment position="end">г</InputAdornment> }}
                    />
                  </Box>
                </Box>
                {/* Розрахунок для кастомного продукту */}
                {customNutrition && <NutritionCells nutrition={customNutrition} sx={{ mt: 1.5 }} />}
              </>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={showDateTimePicker}
                  onChange={(e) => {
                    const isChecked = e.target.checked;
                    setShowDateTimePicker(isChecked);
                    if (isChecked) {
                      const now = new Date();
                      if (!scheduledAt || scheduledAt.getTime() < now.getTime()) {
                        setScheduledAt(now);
                      }
                    } else {
                      setScheduledAt(null);
                    }
                  }}
                  name="scheduleEvent"
                  color="primary"
                />
              }
              label="Запланувати на інший час"
              sx={{ mt: 2 }}
            />

            {showDateTimePicker && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Запланований час"
                  value={scheduledAt}
                  onChange={(newValue) => setScheduledAt(newValue)}
                  slots={{ textField: TextField }}
                  slotProps={{ textField: { variant: 'standard', fullWidth: true, sx: { mt: 1 } } }}

                />
              </LocalizationProvider>
            )}
          </>
        )}
    </BaseDialog>
  );
};
